import { notifier } from "../../core/notifier/notifier";
import { spinner } from "../../util/spinner/spinner";
import { translator } from "../../translation/translator";
import { userProfilePasswordForm } from "./user-profile-password-form";
import {
    UserProfilePasswordApi,
    type UserProfilePasswordUpdateRequest,
    type UserProfilePasswordUpdateResponse,
} from "./user-profile-password-api";
import { UserProfilePasswordListener } from "./user-profile-password-listener";

export class UserProfilePasswordSetup {
    public static validate(form: HTMLFormElement): boolean {
        const passwordEl = userProfilePasswordForm.getPasswordInput(form);
        const confirmationEl = userProfilePasswordForm.getPasswordConfirmationInput(form);

        if (!(passwordEl instanceof HTMLInputElement) || !(confirmationEl instanceof HTMLInputElement)) {
            notifier.error("password_form_validation_failed");
            return false;
        }

        passwordEl.setCustomValidity("");
        confirmationEl.setCustomValidity("");

        const password = String(passwordEl.value || "");
        const confirmation = String(confirmationEl.value || "");

        if (password.length < 8) {
            const policyMessage = translator.getTranslation("password_policy_hint");
            passwordEl.setCustomValidity(policyMessage);
            form.reportValidity();
            notifier.warn("password_policy_hint", { duration: 5000 });
            passwordEl.setCustomValidity("");
            return false;
        }

        if (!form.reportValidity()) {
            return false;
        }

        if (password !== confirmation) {
            const mismatchMessage = translator.getTranslation("password_confirmation_mismatch");
            confirmationEl.setCustomValidity(mismatchMessage);
            form.reportValidity();
            notifier.warn("password_confirmation_mismatch", { duration: 5000 });
            confirmationEl.setCustomValidity("");
            return false;
        }

        return true;
    }

    public static init(el: Element): void {
        if (!(el instanceof HTMLFormElement)) return;
        if (el.dataset.passwordApiBound === "1") return;

        el.dataset.passwordApiBound = "1";
        UserProfilePasswordListener.bindSubmit(el, async (event: Event) => {
            event.preventDefault();
            await this.submit(el);
        });
    }

    public static async submit(form: HTMLFormElement): Promise<void> {
        const apiUrl = form.dataset.apiUrl || "";
        if (!apiUrl) {
            notifier.error("password_update_failed");
            return;
        }

        if (!this.validate(form)) {
            return;
        }

        const submitBtn = userProfilePasswordForm.getSubmitButton(form);
        if (submitBtn instanceof HTMLButtonElement) {
            spinner.showButtonLoader(submitBtn, { translateKey: "spinner_proccesing" });
        }

        try {
            const rawPayload = Object.fromEntries(new FormData(form).entries()) as Record<string, unknown>;
            delete rawPayload._token;
            const payload = rawPayload as UserProfilePasswordUpdateRequest;
            const response = await UserProfilePasswordApi.updatePassword(apiUrl, payload);

            if (!response.ok) {
                await notifier.fromResponse(response, "password_update_failed");
                return;
            }

            const data = (await response.json().catch(() => ({}))) as UserProfilePasswordUpdateResponse;
            notifier.success(data?.message || translator.getTranslation("password_change_completed"));

            for (const input of userProfilePasswordForm.getPasswordInputs(form)) {
                if (input instanceof HTMLInputElement) {
                    input.value = "";
                }
            }
        } catch (error) {
            console.error("Password update error:", error);
            notifier.error("password_update_failed");
        } finally {
            if (submitBtn instanceof HTMLButtonElement) {
                spinner.hideButtonLoader(submitBtn);
            }
        }
    }
}
